import React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import styled from 'styled-components';

import { getEmailMatchingRecords, getEmailMatchingReviewers, getEmailMatchingTabsMetrics } from 'apis/EmailMatchingAPI';
import { useRootContext } from 'components/layout/RootContext';
import { PaginatedResponseType, SelectAdvancedFilterOptionType } from 'types/common';
import useDataList from 'utils/hooks/useDataList';
import { useDebounce } from 'utils/hooks/useDebounce';

import { EMAIL_MATCHING_PAGE_SIZE } from './constants';
import EmailMatchingControls from './EmailMatchingControls';
import EmailMatchingHeader from './EmailMatchingHeader';
import EmailMatchingTable from './EmailMatchingTable';
import {
  EmailMatchingFiltersType,
  EmailMatchingRecordType,
  EmailMatchingTabsMetricsType,
  EmailMatchingUserType,
} from './types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 10px 30px;
  gap: 20px;
`;

const EmailMatching = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useRootContext();

  const { items, totalItemsCount, filters, setItems, setPage, setSearch, setFilters } = useDataList({
    initialState: {
      filters: {
        page: 1,
        pageSize: EMAIL_MATCHING_PAGE_SIZE,
        search: '',
        status: [],
        source: [],
        emailTypes: [],
        confidenceRange: [],
        reviewers: [],
      },
    },
  });

  const emailMatchingFilters = filters as EmailMatchingFiltersType;
  const debouncedSearch = useDebounce(emailMatchingFilters.search, 300);

  const { isLoading } = useQuery<PaginatedResponseType<EmailMatchingRecordType>>({
    queryKey: [
      'email-matching-records',
      emailMatchingFilters.page,
      emailMatchingFilters.pageSize,
      debouncedSearch,
      emailMatchingFilters.status,
      emailMatchingFilters.source,
      emailMatchingFilters.emailTypes,
      emailMatchingFilters.confidenceRange,
      emailMatchingFilters.reviewers,
    ],
    queryFn: () =>
      getEmailMatchingRecords({
        page: emailMatchingFilters.page,
        page_size: emailMatchingFilters.pageSize,
        search: debouncedSearch.trim() || undefined,
        status__in: emailMatchingFilters.status.length ? emailMatchingFilters.status : undefined,
        source__in: emailMatchingFilters.source.length ? emailMatchingFilters.source : undefined,
        communication_types__overlap: emailMatchingFilters.emailTypes.length
          ? emailMatchingFilters.emailTypes
          : undefined,
        confidence_range: emailMatchingFilters.confidenceRange.length
          ? emailMatchingFilters.confidenceRange
          : undefined,
        confirmed_by__in: emailMatchingFilters.reviewers.length ? emailMatchingFilters.reviewers : undefined,
      }),
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    onSuccess: (response) => {
      setItems(response);
    },
    onError: (error: any) => {
      if (error?.response?.status === 403) {
        showMessage('error', 'You do not have permission to view Email Matching.');
      }
    },
  });

  const { data: tabsMetrics } = useQuery<EmailMatchingTabsMetricsType>({
    queryKey: [
      'email-matching-tabs-metrics',
      debouncedSearch,
      emailMatchingFilters.source,
      emailMatchingFilters.emailTypes,
      emailMatchingFilters.confidenceRange,
      emailMatchingFilters.reviewers,
    ],
    queryFn: () =>
      getEmailMatchingTabsMetrics({
        search: debouncedSearch.trim() || undefined,
        source__in: emailMatchingFilters.source.length ? emailMatchingFilters.source : undefined,
        communication_types__overlap: emailMatchingFilters.emailTypes.length
          ? emailMatchingFilters.emailTypes
          : undefined,
        confidence_range: emailMatchingFilters.confidenceRange.length
          ? emailMatchingFilters.confidenceRange
          : undefined,
        confirmed_by__in: emailMatchingFilters.reviewers.length ? emailMatchingFilters.reviewers : undefined,
      }),
    refetchOnReconnect: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const { data: reviewers } = useQuery<EmailMatchingUserType[]>({
    queryKey: ['email-matching-reviewers'],
    queryFn: () => getEmailMatchingReviewers(),
    refetchOnWindowFocus: false,
  });

  const reviewerOptions: SelectAdvancedFilterOptionType[] = (reviewers ?? []).map((reviewer) => ({
    value: reviewer.id,
    label: reviewer.full_name || reviewer.email,
  }));

  const handleUploadSuccess = async () => {
    await queryClient.invalidateQueries(['email-matching-records']);
    await queryClient.invalidateQueries(['email-matching-tabs-metrics']);
  };

  return (
    <Container>
      <EmailMatchingHeader />
      <Content>
        <EmailMatchingControls
          search={emailMatchingFilters.search}
          statusFilters={emailMatchingFilters.status}
          onSearchChange={setSearch}
          onStatusFiltersChange={(values) => setFilters({ status: values })}
          sourceFilters={emailMatchingFilters.source}
          onSourceFiltersChange={(values) => setFilters({ source: values })}
          emailTypeFilters={emailMatchingFilters.emailTypes}
          onEmailTypeFiltersChange={(values) => setFilters({ emailTypes: values })}
          confidenceRange={emailMatchingFilters.confidenceRange}
          onConfidenceRangeChange={(values) => setFilters({ confidenceRange: values })}
          reviewerFilters={emailMatchingFilters.reviewers}
          reviewerOptions={reviewerOptions}
          onReviewerFiltersChange={(values) => setFilters({ reviewers: values })}
          tabMetrics={tabsMetrics ?? { all: 0, matched: 0, needs_review: 0, no_match: 0, spam: 0 }}
          onUploadSuccess={handleUploadSuccess}
        />
        <EmailMatchingTable
          items={(items as EmailMatchingRecordType[]) || []}
          loading={isLoading}
          totalItemsCount={totalItemsCount}
          filters={emailMatchingFilters}
          onPageChange={setPage}
        />
      </Content>
    </Container>
  );
};

export default EmailMatching;
